import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import FolderIcon from '@mui/icons-material/Folder';
import { projectApi, phaseApi, featureApi } from '../api/endpoints';
import { getApiErrorMessage } from '../api/client';
import { useProjects } from '../context/ProjectContext';
import { AcceptanceScreen } from './Acceptance/AcceptanceScreen';
import { usePermission } from '../auth/usePermission';
import type { Project, ProjectStatus } from '../types';

const PROJECTS_PAGE_SIZE = 12;

const PROJECT_STATUS_META: Record<ProjectStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' }> = {
  DRAFT: { label: 'Nháp', color: 'default' },
  IN_PROGRESS: { label: 'Đang triển khai', color: 'primary' },
  COMPLETED: { label: 'Hoàn thành', color: 'success' },
  ARCHIVED: { label: 'Lưu trữ', color: 'warning' },
};

/** Danh sách dự án — bấm vào một dự án để vào màn hình nghiệm thu. */
export function Projects(): JSX.Element {
  const [projects, setProjects] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const canViewDocuments = usePermission('PROJECT_DOCUMENT_VIEW');
  const canCreateFeatureRequest = usePermission('FEATURE_REQUEST_CREATE');

  const handleCardOpen = (project: Project) => {
    navigate(canViewDocuments ? `/projects/${project.id}/documents` : `/projects/${project.id}`);
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    projectApi
      .list({ limit: PROJECTS_PAGE_SIZE, offset: (page - 1) * PROJECTS_PAGE_SIZE })
      .then((result) => {
        if (active) {
          setProjects(result.items);
          setTotal(result.total);
        }
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err, 'Không thể tải danh sách dự án.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page]);

  const pageCount = Math.max(1, Math.ceil(total / PROJECTS_PAGE_SIZE));

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Dự án
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : projects.length === 0 ? (
        <Alert severity="info">Chưa có dự án nào.</Alert>
      ) : (
        <Grid container spacing={2}>
          {projects.map((project) => {
            const meta = PROJECT_STATUS_META[project.status];
            return (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea
                    onClick={() => handleCardOpen(project)}
                    sx={{ flexGrow: 1, alignItems: 'flex-start' }}
                  >
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <FolderIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                          {project.name}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          minHeight: 40,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {project.description || 'Chưa có mô tả.'}
                      </Typography>
                      <Chip label={meta.label} color={meta.color} size="small" />
                    </CardContent>
                  </CardActionArea>
                  <CardActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', pt: 0 }}>
                    {canViewDocuments && (
                      <Button size="small" onClick={() => navigate(`/projects/${project.id}/documents`)}>
                        Chi tiết
                      </Button>
                    )}
                    <Button size="small" onClick={() => navigate(`/projects/${project.id}`)}>
                      Nghiệm thu dự án
                    </Button>
                    {canCreateFeatureRequest && (
                      <Button size="small" onClick={() => navigate(`/feature-requests?projectId=${project.id}`)}>
                        Tạo yêu cầu chức năng
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {!loading && !error && total > PROJECTS_PAGE_SIZE && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Stack>
      )}
    </Box>
  );
}

/** Màn hình nghiệm thu khách hàng của một dự án (route /projects/:id). */
export function ProjectDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { setSelectedProjectId } = useProjects();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * URL là nguồn sự thật ở trang này: mở dự án bằng bất kỳ đường nào (bấm thẻ,
   * "Mở tính năng" từ bug, dán link) đều kéo ô chọn trên TopBar về đúng dự án,
   * để thanh trên không nói một đằng còn nội dung một nẻo.
   *
   * Chỉ đồng bộ SAU KHI tải thành công, không set ngay từ useParams: link tới
   * dự án người dùng không có quyền sẽ trả 403, mà set sớm thì đẩy một id không
   * có trong danh sách vào <Select> và sinh cảnh báo out-of-range của MUI.
   */
  useEffect(() => {
    if (project) setSelectedProjectId(project.id);
  }, [project, setSelectedProjectId]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    setError(null);
    projectApi
      .get(id)
      .then((p) => {
        if (active) setProject(p);
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err, 'Không thể tải thông tin dự án.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  if (!id) {
    return <Alert severity="error">Thiếu mã dự án.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return <Alert severity="error">{error ?? 'Không tìm thấy dự án.'}</Alert>;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/projects" underline="hover" color="inherit">
          Dự án
        </Link>
        <Typography color="text.primary">{project.name}</Typography>
      </Breadcrumbs>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {project.name}
      </Typography>
      {project.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {project.description}
        </Typography>
      )}
      <AcceptanceScreen projectId={id} />
    </Box>
  );
}

/** Wrapper mỏng: mở màn hình nghiệm thu tại một giai đoạn cụ thể (route /phases/:id). */
export function PhaseDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    phaseApi
      .get(id)
      .then((phase) => {
        if (active) {
          navigate(`/projects/${phase.project_id}?phaseId=${phase.id}`, { replace: true });
        }
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err, 'Không tìm thấy giai đoạn.'));
      });
    return () => {
      active = false;
    };
  }, [id, navigate]);

  if (error) return <Alert severity="error">{error}</Alert>;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress />
    </Box>
  );
}

/** Wrapper mỏng: mở màn hình nghiệm thu tại một tính năng cụ thể (route /features/:id). */
export function FeatureDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    featureApi
      .get(id)
      .then(async (feature) => {
        const phase = await phaseApi.get(feature.phase_id);
        if (active) {
          navigate(`/projects/${phase.project_id}?phaseId=${phase.id}&featureId=${feature.id}`, {
            replace: true,
          });
        }
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err, 'Không tìm thấy tính năng.'));
      });
    return () => {
      active = false;
    };
  }, [id, navigate]);

  if (error) return <Alert severity="error">{error}</Alert>;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress />
    </Box>
  );
}
